// Sign in from the link in a sign-in code's email (PLAN-2026-10-28-by-email
// §3.4, scenario A6, B-547).
//
// The link is `/sign-in#<token>`. The token rides in the fragment, as a reset's
// does, because a browser sends a fragment nowhere, and this screen takes it
// out of the address bar as soon as it has read it.
//
// IT DOES NOT SIGN IN ON OPENING, as the stop links do not stop on opening:
// a mail scanner that opens every link in a message would spend it, signing in
// the scanner's browser, which is nobody's — and the link works once, so the
// person would then find it used. So the press is the person's: one button,
// *Sign in*, on a page that says where.
//
// Three states, one card each:
//   press   the link read: one press signs this browser in;
//   ask     no link, or one that has run out: an email for a new code;
//   sent    what happens next, worded for any address.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../data/session';
import { useTier } from '../shell/useTier';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { Text, TextField, tell } from '../kit/ui';

/** A token as `newToken` makes them: base64url, 43 characters for 32 bytes. */
const TOKEN = /^[A-Za-z0-9_-]{20,}$/;

type Stage = 'press' | 'ask' | 'sent';

export function SignInLinkScreen() {
  const { signInWithLink, requestCode } = useSession();
  const tier = useTier();
  const navigate = useNavigate();
  const location = useLocation();
  // Read once, on the first render: the effect below clears the bar.
  const [token] = useState<string | null>(() => {
    const raw = location.hash.replace(/^#/, '');
    return TOKEN.test(raw) ? raw : null;
  });
  const [stage, setStage] = useState<Stage>(token === null ? 'ask' : 'press');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Why the ask card is showing: no link at all, or the server's word on this one. */
  const [why, setWhy] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (location.hash !== '') navigate(location.pathname, { replace: true });
  }, [location.hash, location.pathname, navigate]);

  const press = async () => {
    if (pending || token === null) return;
    setPending(true);
    const outcome = await signInWithLink(token);
    setPending(false);
    if (outcome.ok) {
      tell('You are signed in.', 'good');
      navigate('/', { replace: true });
      return;
    }
    // The link is spent, run out or unknown: the card becomes the way to a
    // new code. A throttle or no connection stays here to try again.
    if (outcome.code === 'unauthorized' || outcome.code === 'invalid') {
      setWhy(outcome.message);
      setError(null);
      setStage('ask');
      return;
    }
    setError(outcome.message);
  };

  const ask = async () => {
    if (pending) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return setError('That email does not look right yet.');
    }
    setPending(true);
    const outcome = await requestCode(email);
    setPending(false);
    if (!outcome.ok) return setError(outcome.message);
    setError(null);
    setStage('sent');
  };

  const steps: Record<Stage, WizardStep> = {
    press: {
      id: 'press',
      title: 'Sign in',
      hint: 'This signs you in on this device, in this browser.',
      content: (
        <Text tone="secondary" size="sm" block>
          The link works once. If you asked for it on another device, type the code from the same
          email there instead.
        </Text>
      ),
      error,
      doneLabel: pending ? 'Signing in…' : 'Sign in',
    },
    ask: {
      id: 'ask',
      title: why === null ? 'Get a sign-in code' : 'That link will not work',
      hint:
        why === null
          ? 'This page opens from the link in a sign-in email. Type the email you sign in with and we will send a code.'
          : `${why} Type the email you sign in with and we will send a new code.`,
      content: (
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(next) => {
            setError(null);
            setEmail(next);
          }}
          autoFocus
        />
      ),
      error,
      doneLabel: pending ? 'Sending…' : 'Email me a code',
    },
    sent: {
      id: 'sent',
      title: 'Check your email',
      hint: `If ${email.trim()} has an account here, a code and a link are on their way. They work for 15 minutes.`,
      content: (
        <Text tone="secondary" size="sm" block>
          Tap the link in that email on this device to sign in here.
        </Text>
      ),
      doneLabel: 'Done',
    },
  };

  // ITS OWN FRAME, as the reset's page has: this route is outside the shell.
  return (
    <div className="fsp-frame" data-tier={tier}>
      <Wizard
        title="Sign in by email"
        steps={[steps[stage]]}
        index={0}
        onIndex={() => undefined}
        onDone={() => {
          if (stage === 'press') void press();
          else if (stage === 'ask') void ask();
          else navigate('/', { replace: true });
        }}
        onCancel={() => navigate('/', { replace: true })}
      />
    </div>
  );
}
